// app/javascript/components/Chat/DataOutput.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  SwipeableDrawer,
  useMediaQuery,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useChatContext } from "../../contexts/ChatContext";
import { useAppContext } from "../../contexts/AppContext";
import { LoadingPhasesBox } from "./ResponseWaiting";
import { isDarkMode } from "../../utils/constants";
import MarketProLogo from "../../../javascript/images/MarketProLogo.png";
import MarketProLogoLight from "../../../javascript/images/MarketProLogoLight.png";
import { keyframes } from "@emotion/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const pulse = keyframes`
  0%   { transform: scale(0.65); opacity: 0.5; }
  70%  { transform: scale(1.0); opacity: 0; }
  100% { transform: scale(1.0); opacity: 0; }
`;
const helpPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(49, 122, 255, 0.45); }
  70% { box-shadow: 0 0 0 14px rgba(49, 122, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(49, 122, 255, 0); }
`;

const scanSweep = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  18% { opacity: 0.4; }
  100% { transform: translateX(130%); opacity: 0; }
`;

const processCardDropIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-14px) scale(0.985);
  }
  65% {
    opacity: 1;
    transform: translateY(1px) scale(1);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ProcessQueueCard = ({
  text,
  done = false,
  isActive = false,
  phaseMeta,
  enterIndex = 0,
}) => {
  const isSearchPhase = phaseMeta.kind === "search";
  const eyebrow = done ? "Process Complete" : (isActive ? "Current Process" : "Data Process");
  const iconBackground = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.22)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase
      ? (isDarkMode ? "rgba(34,211,166,0.16)" : "rgba(34,211,166,0.12)")
      : (isDarkMode ? "rgba(36,157,255,0.16)" : "rgba(36,157,255,0.12)"));
  const iconColor = done
    ? (isDarkMode ? "#B9F6CF" : "#237A48")
    : phaseMeta.accent;
  const sweepBackground = isSearchPhase
    ? "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(113,255,223,0.13) 45%, rgba(255,255,255,0) 100%)"
    : "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(133,196,255,0.12) 45%, rgba(255,255,255,0) 100%)";
  const indicatorGlow = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.18)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase ? "rgba(34,211,166,0.12)" : "rgba(36,157,255,0.12)");

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mt: 1,
        mb: 1.4,
        py: 1.35,
        px: 1.1,
        borderRadius: 3,
        border: `1px solid ${phaseMeta.panelBorder}`,
        background: phaseMeta.panelBackground,
        boxShadow: phaseMeta.panelShadow,
        animation: `${processCardDropIn} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${Math.min(enterIndex, 6) * 48}ms`,
        willChange: "transform, opacity",
        transition: "border-color 220ms ease, background 220ms ease, box-shadow 220ms ease",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transition: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: sweepBackground,
          opacity: done ? 0.55 : 1,
          animation: isActive ? `${scanSweep} ${isSearchPhase ? 2.6 : 3.2}s ease-in-out infinite` : "none",
        }}
      />

      <Box sx={{ position: "relative", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              flexShrink: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBackground,
              color: iconColor,
            }}
          >
            {done ? (
              <CheckRoundedIcon sx={{ fontSize: 12 }} />
            ) : isSearchPhase ? (
              <PublicRoundedIcon sx={{ fontSize: 12 }} />
            ) : (
              <AutorenewRounded
                sx={{
                  fontSize: 12,
                  animation: isActive ? `${spin} 1.2s linear infinite` : "none",
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: 1.1,
                textTransform: "uppercase",
                color: phaseMeta.panelMutedColor,
                fontFamily: '"Sora", "Avenir Next", "Montserrat", sans-serif',
              }}
            >
              {eyebrow}
            </Typography>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: iconColor,
                boxShadow: `0 0 0 3px ${indicatorGlow}`,
              }}
            />
          </Box>
          <Typography
            sx={{
              mt: 0.45,
              fontSize: "14px",
              lineHeight: 1.35,
              fontWeight: 600,
              color: phaseMeta.panelTextColor,
              fontFamily: '"Sora", "Avenir Next", "Montserrat", sans-serif',
            }}
          >
            {text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};


const DataOutput = ({ loadingResponse }) => {
  const [agenticOutputs, setAgenticOutputs] = useState([]);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpTeaserDismissed, setHelpTeaserDismissed] = useState(false);
  const [showHelpTeaser, setShowHelpTeaser] = useState(false);
  const {
    loadingPhaseMessage,
    dataProcessesQueue,
    isDataProcessesOpen,
    setIsDataProcessesOpen,
    activeThread,
    liveTextOutputting,
  } =
    useChatContext();
  const { messagesByThreadUuid, messageThreads } = useAppContext();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const showHelpTeaserText = useMediaQuery(theme.breakpoints.up("sm"));
  const showInciteHelp = false;
  const PANEL_WIDTH = 280;
  const helpRightOffset = (isDesktop && isDataProcessesOpen ? PANEL_WIDTH : 0) + 56;

  const [doneByMsg, setDoneByMsg] = useState(() => new Map());
  const seenIdsRef = useRef(new Set());
  const prevThreadUuidRef = useRef(null);

  const activeThreadUuid = activeThread?.threadUuid ?? null;
  const showProcessingLoader = loadingResponse && !liveTextOutputting;
  const threadMessages = activeThreadUuid
    ? (messagesByThreadUuid?.[activeThreadUuid] || [])
    : (messageThreads || []);
  const hasAskedFirstQuestion = threadMessages.some((message) => {
    const sender = String(message?.sender || "").toLowerCase();
    const text = String(message?.message || "").trim();
    return (sender === "human" || sender === "user") && text.length > 0 && !message?.isButton;
  });

  const shouldPulseHelp = hasAskedFirstQuestion && !helpTeaserDismissed;

  useEffect(() => {
    if (!shouldPulseHelp) {
      setShowHelpTeaser(false);
      return;
    }
    const timer = setTimeout(() => setShowHelpTeaser(true), 280);
    return () => clearTimeout(timer);
  }, [shouldPulseHelp, activeThreadUuid]);

  useEffect(() => {
    if (!dataProcessesQueue?.length) return;
    setAgenticOutputs((prev) => {
      const next = [...prev];
      for (const evt of dataProcessesQueue) {
        if (!evt?.id) continue;
        if (!seenIdsRef.current.has(evt.id)) {
          seenIdsRef.current.add(evt.id);
          if (evt.text.complete || evt.text.require_completion == false) {
            let timeout = 800;
            if (evt.text.require_completion == false) timeout = 1300
            setTimeout(() => {
              setDoneByMsg((prevMap) => {
                const m = new Map(prevMap);
                m.set(evt.text.message, true);
                return m;
              });
            }, timeout)
          }
          if (!evt.text.complete) {
            next.push(evt.text.message);
          }
        }
      }
      return next;
    });
  }, [dataProcessesQueue]); // depend on the array reference, not just length

  useEffect(() => {
    if (loadingResponse) {
      setAgenticOutputs([]);
      setDoneByMsg(new Map());
    }
  }, [loadingResponse]);

  useEffect(() => {
    const curr = activeThread?.threadUuid ?? null;
    const prev = prevThreadUuidRef.current;
    // if thread changes, reset it (If it doesn't change from a temp thread).
    if (prev === curr) return; // only run if thread uuid has changed..
    const prevWasTemp = !!prev && prev.startsWith("temp-");
    if (!prevWasTemp) {
      if (activeThread) {
        setAgenticOutputs([])
      }
    }
    prevThreadUuidRef.current = curr;
  }, [activeThread])

  const phaseMeta = getLoadingPhaseMeta(loadingPhaseMessage || "", isDarkMode);

  const processContent = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        py: 2,
        px: 1,
        pt: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        "& *": { whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" },
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "#3a3a3a" : "#cfcfcf",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: isDarkMode ? "#1e1e1e" : "#f3f3f3",
        },
      }}
    >
      {agenticOutputs.length === 0 && (
        <LoadingPhasesBox
          text={loadingPhaseMessage ? "Awaiting next process." : "Awaiting analysis."}
          sx={{ marginTop: "8px" }}
          hideDotDotDot={!showProcessingLoader}
          preventAnimation={!showProcessingLoader}
        />
      )}

      {agenticOutputs.map((msg, idx) => {
        const done = !!doneByMsg.get(msg);
        const isActive =
          !done && showProcessingLoader && idx === agenticOutputs.length - 1;
        return (
          <ProcessQueueCard
            key={`${idx}-${msg}`}
            text={msg}
            done={done}
            isActive={isActive}
            phaseMeta={phaseMeta}
            enterIndex={idx}
          />
        );
      })}
    </Box>
  );

  return (
    <>
      {showInciteHelp && (
        <Box
          sx={{
            position: "fixed",
            top: 8,
            right: helpRightOffset,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            pointerEvents: "none",
            transition: "right 240ms ease",
          }}
        >
          {showHelpTeaserText && (
            <Box
              sx={{
                pointerEvents: "none",
                whiteSpace: "nowrap",
                px: 1.5,
                py: 0.75,
                borderRadius: "999px",
                border: `1px solid ${isDarkMode ? "rgba(72, 138, 255, 0.45)" : "rgba(26, 101, 230, 0.28)"}`,
                background: isDarkMode
                  ? "linear-gradient(110deg, rgba(18,25,40,0.92), rgba(14,18,28,0.9))"
                  : "linear-gradient(115deg, rgba(247,252,255,0.97), rgba(230,240,255,0.95))",
                boxShadow: isDarkMode
                  ? "0 8px 22px rgba(0,0,0,0.32)"
                  : "0 8px 22px rgba(20,75,170,0.15)",
                transformOrigin: "right center",
                transform: showHelpTeaser ? "translateX(0px) scale(1)" : "translateX(18px) scale(0.96)",
                opacity: showHelpTeaser ? 1 : 0,
                maxWidth: showHelpTeaser ? 420 : 0,
                overflow: "hidden",
                transition:
                  "transform 340ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease, max-width 340ms ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: 0.1,
                  color: isDarkMode ? "#D8E7FF" : "#104C9A",
                }}
              >
                Learn how to make money with Incite?
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={() => {
              setIsHelpOpen(true);
              setHelpTeaserDismissed(true);
            }}
            aria-label="Open Incite help"
            sx={{
              pointerEvents: "auto",
              width: 38,
              height: 38,
              color: "#fff",
              background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
              border: "1px solid rgba(255,255,255,0.28)",
              boxShadow: "0 10px 26px rgba(25, 93, 210, 0.35)",
              animation: shouldPulseHelp ? `${helpPulse} 1.8s ease-out infinite` : "none",
              "&:hover": {
                background: "linear-gradient(135deg, #2f90ff 0%, #1c6af4 100%)",
              },
            }}
          >
            <HelpRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      {/* Floating launcher */}
      <Box
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          pointerEvents: "none",
        }}
      >
        <IconButton
          onClick={() => setIsDataProcessesOpen((o) => !o)}
          sx={{
            pointerEvents: "auto",
            position: "relative",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2b2b2b" : "#D6D6D6",
            },
            ...(showProcessingLoader && {
              // subtle pulsing ring
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: isDarkMode ? "3px solid rgba(255,255,255,0.55)" : "2px solid rgba(0,0,0,0.45)",
                animation: `${pulse} 1.5s ease-out infinite`,
                pointerEvents: "none",
              },
            }),
          }}
          aria-label={
            isDataProcessesOpen ? "Collapse data output" : "Expand data output"
          }
          size="small"
        >
          <Box
            component="img"
            src={isDarkMode ? MarketProLogo : MarketProLogoLight}
            sx={{
              width: "36px",
              animation: false ? `${spin} 40s linear infinite` : "none",
            }}
            className="logo-img"
            alt="IncitePlus Logo"
          />
        </IconButton>
      </Box>

      {showInciteHelp && (
        <Dialog
          open={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: "hidden",
              border: `1px solid ${isDarkMode ? "rgba(62,84,118,0.55)" : "rgba(163,189,233,0.55)"}`,
              background: isDarkMode
                ? "linear-gradient(180deg, #131a28 0%, #0d111b 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                pr: { xs: 5, sm: 6 },
                position: "relative",
                borderBottom: `1px solid ${isDarkMode ? "rgba(68,85,112,0.45)" : "rgba(187,208,243,0.65)"}`,
                background: isDarkMode
                  ? "radial-gradient(120% 120% at 100% 0%, rgba(39,129,255,0.3) 0%, rgba(17,27,43,0) 62%)"
                  : "radial-gradient(130% 130% at 100% 0%, rgba(47,127,255,0.2) 0%, rgba(255,255,255,0) 62%)",
              }}
            >
              <IconButton
                onClick={() => setIsHelpOpen(false)}
                aria-label="Close help dialog"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  color: isDarkMode ? "#D5E4FF" : "#23529A",
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  fontWeight: 700,
                  color: isDarkMode ? "#94B8F6" : "#2D66B7",
                }}
              >
                Incite Guide
              </Typography>
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: { xs: "26px", sm: "31px" },
                  lineHeight: 1.08,
                  fontWeight: 700,
                  color: isDarkMode ? "#F3F8FF" : "#0F294F",
                  fontFamily: '"Sora", "Avenir Next", "Segoe UI", sans-serif',
                }}
              >
                Learn how to make money with Incite.
              </Typography>
              <Typography
                sx={{
                  mt: 1.1,
                  fontSize: "14px",
                  lineHeight: 1.6,
                  color: isDarkMode ? "#A5BEDF" : "#335B8F",
                  maxWidth: 520,
                }}
              >
                This walkthrough will show how to ask better questions, interpret signals faster, and convert answers
                into clear trading actions.
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 2.25, sm: 3 }, display: "grid", gap: 1.2 }}>
              {[
                "How Incite turns your question into opportunity scans",
                "How to spot the highest-value insights in every response",
                "How to turn signals into a repeatable decision process",
              ].map((item, idx) => (
                <Box
                  key={item}
                  sx={{
                    p: 1.3,
                    borderRadius: 2,
                    border: `1px solid ${isDarkMode ? "rgba(85,105,138,0.4)" : "rgba(176,201,239,0.62)"}`,
                    backgroundColor: isDarkMode ? "rgba(18,27,42,0.62)" : "rgba(247,251,255,0.95)",
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
                      mt: 0.15,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      lineHeight: 1.55,
                      color: isDarkMode ? "#E5EEFF" : "#123A67",
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                px: { xs: 2.25, sm: 3 },
                pb: { xs: 2.25, sm: 3 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: isDarkMode ? "#86A2CB" : "#567CAE" }}>
                Walkthrough content is ready for your next step.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setIsHelpOpen(false)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "999px",
                  px: 2,
                  background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
                }}
              >
                Got it
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

    {isDesktop ? (
      <Drawer
        anchor="right"
        variant="persistent"
        open={isDataProcessesOpen}
        sx={{
          "& .MuiDrawer-paper": {
            width: PANEL_WIDTH,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            Data Processes
          </Typography>
        </Box>
        {processContent}
      </Drawer>
    ) : (
      <SwipeableDrawer
        anchor="right"
        open={isDataProcessesOpen}
        onOpen={() => setIsDataProcessesOpen(true)}
        onClose={() => setIsDataProcessesOpen(false)}
        swipeAreaWidth={24}
        keepMounted
        sx={{
          "& .MuiDrawer-paper": {
            width: PANEL_WIDTH,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            Data Processes
          </Typography>
        </Box>
        {processContent}
      </SwipeableDrawer>
    )}
    </>
  );
};

export default DataOutput;
